import { z } from "zod";

/** Ordered from least to most sensitive. Order matters: merges take the max. */
export const SENSITIVITY_CLASSES = [
  "PUBLIC",
  "LOW_RISK",
  "BUSINESS_DATA",
  "INTERNAL",
  "CONFIDENTIAL",
  "PII",
  "FINANCIAL",
  "SENSITIVE_PII",
  "HEALTH",
  "AUTHENTICATION_SECRET",
  "PROHIBITED",
] as const;
export const SensitivityClass = z.enum(SENSITIVITY_CLASSES);
export type SensitivityClass = z.infer<typeof SensitivityClass>;

export function sensitivityRank(c: SensitivityClass): number {
  return SENSITIVITY_CLASSES.indexOf(c);
}

export function maxSensitivity(a: SensitivityClass, b: SensitivityClass): SensitivityClass {
  return sensitivityRank(a) >= sensitivityRank(b) ? a : b;
}

/** Classes that are default-deny: they may never be shared raw without an explicit, audited override. */
export const DEFAULT_DENY: ReadonlySet<SensitivityClass> = new Set([
  "INTERNAL",
  "CONFIDENTIAL",
  "PII",
  "FINANCIAL",
  "SENSITIVE_PII",
  "HEALTH",
  "AUTHENTICATION_SECRET",
  "PROHIBITED",
]);

/** Classes that can NEVER be exported, not even with an override (hash/tokenise also disallowed). */
export const NEVER_EXPORT: ReadonlySet<SensitivityClass> = new Set(["AUTHENTICATION_SECRET", "PROHIBITED"]);

export const ColumnClassification = z.object({
  table: z.string(),
  column: z.string(),
  sensitivity: SensitivityClass,
  /** Semantic type, e.g. "email", "price", "sku", "geo.city". */
  semanticType: z.string().optional(),
  confidence: z.number().min(0).max(1),
  reasons: z.array(z.string()).max(10),
  /** Which classifier(s) produced this: deterministic rules and/or model. */
  sources: z.array(z.enum(["rules", "model", "human"])).min(1),
  /** Quasi-identifier: innocent alone, identifying in combination (zip+dob+gender). */
  quasiIdentifier: z.boolean().default(false),
});
export type ColumnClassification = z.infer<typeof ColumnClassification>;
